//src/components/BotSettings/SellTakeProfitStages.tsx
// src/components/BotSettings/SellTakeProfitStages.tsx
import React from 'react';
import { motion } from 'framer-motion';
import { Target, Plus, Trash2, AlertCircle, Shield, ArrowDownRight } from 'lucide-react';
import { cn } from '@/utils/cn';

export const SellTakeProfitStages: React.FC<any> = ({
  masterEnabled = true,
  quoteCurrency = '',
  sellTakeProfitStagesEnabled = false,
  onSellTakeProfitStagesEnabledChange = () => {},
  sellTakeProfitStagesUseFixedAmount = false,
  onSellTakeProfitStagesUseFixedAmountChange = () => {},
  sellTakeProfitStagesAutoAdjustEnabled = true,
  onSellTakeProfitStagesAutoAdjustEnabledChange = () => {},
  sellTakeProfitStagesDynamicTrailingEnabled = true,
  onSellTakeProfitStagesDynamicTrailingEnabledChange = () => {},
  sellTakeProfitStagesInitialTrailingStop = '',
  onSellTakeProfitStagesInitialTrailingStopChange = () => {},
  sellTakeProfitStagesData = [],
  onSellTakeProfitStagesDataChange = () => {}
}) => {
  const safeStages = Array.isArray(sellTakeProfitStagesData) ? sellTakeProfitStagesData : [];
  const isEffectivelyEnabled = masterEnabled && sellTakeProfitStagesEnabled;

  const handleInitialStopChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!masterEnabled) return;
    onSellTakeProfitStagesInitialTrailingStopChange(e.target.value);
  };

  const handleStageChange = (index: number, field: string, value: string) => {
    if (!masterEnabled) return;
    const nextStages = [...safeStages];
    nextStages[index] = { ...nextStages[index], [field]: value };
    onSellTakeProfitStagesDataChange(nextStages);
  };

  const handleAddStage = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!masterEnabled || safeStages.length >= 5) return;
    const newStage = {
      stage: safeStages.length + 1,
      target_pct: '',
      target_usd: '',
      sell_pct: '',
      trailing_stop_pct: '',
      enabled: true
    };
    onSellTakeProfitStagesDataChange([...safeStages, newStage]);
  };

  const handleRemoveStage = (index: number, e: React.MouseEvent) => {
    e.preventDefault();
    if (!masterEnabled) return;
    onSellTakeProfitStagesDataChange(safeStages.filter((_, i) => i !== index).map((s, i) => ({ ...s, stage: i + 1 })));
  };

  const totalPct = safeStages.reduce((sum: number, s: any) => sum + (Number(s.sell_pct ?? s.sellPercentage) || 0), 0);
  const isValid = safeStages.length === 0 || totalPct <= 100;

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="space-y-8">
      <div className={cn("bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700 relative", !masterEnabled && "opacity-60")}>
        
        {!masterEnabled && (
          <div className="absolute inset-0 z-10 bg-gray-50/50 dark:bg-gray-900/60 backdrop-blur-[2px] flex items-center justify-center rounded-xl pointer-events-none">
            <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 flex items-center gap-3">
              <AlertCircle className="h-6 w-6 text-gray-400" />
              <div>
                <p className="font-bold text-gray-900 dark:text-white">Profit Management is Disabled</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">Enable "Short Entry Strategy" first.</p>
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Profit Management (Short)</h2>
            <p className="text-gray-600 dark:text-gray-400 mt-1">Progressive profit-taking for short positions</p>
          </div>
          <div className="h-12 w-12 bg-red-100 dark:bg-red-900/20 rounded-lg flex items-center justify-center">
            <ArrowDownRight className="h-6 w-6 text-red-500" />
          </div>
        </div>

        <div className="p-4 bg-red-50 dark:bg-red-900/10 rounded-lg border-2 border-red-200 dark:border-red-800 mb-6 relative z-20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ArrowDownRight className="h-5 w-5 text-red-600 dark:text-red-400" />
              <label className="text-sm font-semibold text-gray-900 dark:text-white block">Enable Profit Management</label>
            </div>
            <button
              type="button"
              disabled={!masterEnabled}
              onClick={() => onSellTakeProfitStagesEnabledChange(!sellTakeProfitStagesEnabled)}
              className={cn("relative inline-flex h-7 w-14 items-center rounded-full transition-colors pointer-events-auto", isEffectivelyEnabled ? "bg-red-600" : "bg-gray-300 dark:bg-gray-600")}
            >
              <span className={cn("inline-block h-5 w-5 transform rounded-full bg-white transition-transform", isEffectivelyEnabled ? "translate-x-8" : "translate-x-1")} />
            </button>
          </div>
        </div>

        {isEffectivelyEnabled && (
          <div className="space-y-6 animate-in fade-in duration-200">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Use fixed amount targets {quoteCurrency ? `(${quoteCurrency})` : ''}</label>
              <button
                type="button"
                onClick={() => onSellTakeProfitStagesUseFixedAmountChange(!sellTakeProfitStagesUseFixedAmount)}
                className={cn("relative inline-flex h-6 w-11 items-center rounded-full transition-colors", sellTakeProfitStagesUseFixedAmount ? "bg-red-500" : "bg-gray-200 dark:bg-gray-700")}
              >
                <span className={cn("inline-block h-4 w-4 transform rounded-full bg-white transition-transform", sellTakeProfitStagesUseFixedAmount ? "translate-x-6" : "translate-x-1")} />
              </button>
            </div>

            <div className="p-4 bg-yellow-50 dark:bg-yellow-900/10 rounded-lg border border-gray-200 dark:border-yellow-800/30 space-y-4">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Shield className="h-5 w-5 text-yellow-500" /> Stop Loss Settings
              </h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Initial Stop Loss {sellTakeProfitStagesUseFixedAmount ? (quoteCurrency ? `(${quoteCurrency})` : '') : '(%)'}</label>
                <input type="number" step={0.01} value={sellTakeProfitStagesInitialTrailingStop ?? ''} onChange={handleInitialStopChange} className="w-full px-3 py-2 border bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 border-gray-300 dark:border-gray-600 rounded-lg" />
              </div>
              <div className="flex items-center justify-between pt-2">
                <label className="text-sm text-gray-700 dark:text-gray-300">Auto-adjust Stop Loss</label>
                <button type="button" onClick={() => onSellTakeProfitStagesAutoAdjustEnabledChange(!sellTakeProfitStagesAutoAdjustEnabled)} className={cn("relative inline-flex h-6 w-11 items-center rounded-full transition-colors", sellTakeProfitStagesAutoAdjustEnabled ? "bg-yellow-500" : "bg-gray-200 dark:bg-gray-700")}>
                  <span className={cn("inline-block h-4 w-4 transform rounded-full bg-white transition-transform", sellTakeProfitStagesAutoAdjustEnabled ? "translate-x-6" : "translate-x-1")} />
                </button>
              </div>
              <div className="flex items-center justify-between">
                <label className="text-sm text-gray-700 dark:text-gray-300">Dynamically adjust Stop Loss</label>
                <button type="button" onClick={() => onSellTakeProfitStagesDynamicTrailingEnabledChange(!sellTakeProfitStagesDynamicTrailingEnabled)} className={cn("relative inline-flex h-6 w-11 items-center rounded-full transition-colors", sellTakeProfitStagesDynamicTrailingEnabled ? "bg-yellow-500" : "bg-gray-200 dark:bg-gray-700")}>
                  <span className={cn("inline-block h-4 w-4 transform rounded-full bg-white transition-transform", sellTakeProfitStagesDynamicTrailingEnabled ? "translate-x-6" : "translate-x-1")} />
                </button>
              </div>
            </div>

            {!isValid && safeStages.length > 0 && (
              <div className="p-4 bg-red-50 text-red-800 rounded-lg text-sm flex items-center">
                <AlertCircle className="h-4 w-4 mr-2" /> Total percentage exceeds 100%
              </div>
            )}

            <div className="space-y-4">
              {safeStages.map((stage: any, index: number) => {
                const targetField = sellTakeProfitStagesUseFixedAmount ? 'target_usd' : 'target_pct';

                return (
                  <div key={index} className="p-4 rounded-lg border bg-gray-50 dark:bg-gray-700/50 border-gray-200 dark:border-gray-600">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <Target className="h-5 w-5 text-red-500" />
                        <h3 className="text-base font-semibold text-gray-900 dark:text-white">Stage {index + 1}</h3>
                      </div>
                      <button type="button" onClick={(e) => handleRemoveStage(index, e)} className="p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors" title="Remove stage">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Target {sellTakeProfitStagesUseFixedAmount ? '($)' : '(%)'}</label>
                        <input type="number" step={0.01} value={stage[targetField] ?? stage.profitTargetPct ?? stage.profitAmountUsd ?? ''} onChange={(e) => handleStageChange(index, targetField, e.target.value)} className="w-full px-4 py-2 border bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 border-gray-300 dark:border-gray-600 rounded-lg" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Sell Pct (%)</label>
                        <input type="number" min={0} max={100} step={0.1} value={stage.sell_pct ?? stage.sellPercentage ?? ''} onChange={(e) => handleStageChange(index, 'sell_pct', e.target.value)} className="w-full px-4 py-2 border bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 border-gray-300 dark:border-gray-600 rounded-lg" />
                      </div>
                      <div className={cn(!sellTakeProfitStagesAutoAdjustEnabled && !sellTakeProfitStagesDynamicTrailingEnabled && "opacity-40")}>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Trailing Stop (%)</label>
                        <input type="number" step={0.01} disabled={!sellTakeProfitStagesAutoAdjustEnabled && !sellTakeProfitStagesDynamicTrailingEnabled} value={stage.trailing_stop_pct ?? stage.trailingStopPct ?? ''} onChange={(e) => handleStageChange(index, 'trailing_stop_pct', e.target.value)} className="w-full px-4 py-2 border bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:cursor-not-allowed focus:ring-2 focus:ring-red-500 border-gray-300 dark:border-gray-600 rounded-lg" />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {safeStages.length < 5 && (
              <button
                type="button"
                onClick={handleAddStage}
                className="w-full py-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-400 hover:border-red-500 hover:text-red-500 transition-colors flex items-center justify-center gap-2"
              >
                <Plus className="h-5 w-5" />
                <span>Add Stage ({safeStages.length}/5)</span>
              </button>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
};